/**
 * Decodage de la charge utile deposee par les Raccourcis iOS.
 *
 * ## Le format, et pourquoi celui-la
 *
 * Une charge utile est une suite de segments separes par des tirets :
 *
 *     P202608102237-V202608102355-O202608110312-A202608110700
 *     ^^-------------- lettre de type
 *      ^^^^^^^^^^^^--- yyyyMMddHHmm en heure LOCALE
 *
 * Le jeu de caracteres est entierement URL-safe. C'est ce qui permet a
 * l'automatisation de se contenter d'un `Format Date` personnalise et d'un
 * `Combine Text` : aucune action d'encodage n'est necessaire. Sur une
 * configuration manuelle de cinq automatisations, chaque action en moins est
 * une source d'erreur en moins.
 *
 * La charge voyage dans le **fragment** de l'URL (`/#/sync/...`), jamais dans
 * la query string. Un fragment n'est pas transmis au serveur : les heures de
 * sommeil n'apparaissent donc dans aucun log Apache. C'est ce qui rend le
 * « zero backend » verifiable par le navigateur, et pas seulement promis.
 *
 * ## Heure locale murale
 *
 * `yyyyMMddHHmm` ne porte pas de fuseau : c'est l'heure affichee par le
 * telephone. La conversion en `Instant` UTC utilise donc le fuseau de
 * l'appareil au moment de l'ingestion.
 *
 * Consequence assumee : lors du passage a l'heure d'hiver, 02 h 30 existe deux
 * fois et `new Date(...)` en choisit une. Une nuit par an peut etre decalee
 * d'une heure. Embarquer une base de fuseaux pour couvrir ce cas couterait
 * plus cher que le defaut qu'il corrige.
 */

import { nowInstant } from '../domain/time'
import type { Evenement, TypeEvenement } from '../domain/types'

/** Lettres emises par les raccourcis. Voir la page Installation. */
const TYPES_PAR_LETTRE: Record<string, TypeEvenement> = {
  O: 'ouverture',
  V: 'fermeture',
  P: 'branche',
  U: 'debranche',
  A: 'alarme',
}

export const LETTRES_PAR_TYPE: Partial<Record<TypeEvenement, string>> = {
  ouverture: 'O',
  fermeture: 'V',
  branche: 'P',
  debranche: 'U',
  alarme: 'A',
}

export interface ResultatDecodage {
  evenements: Evenement[]
  /** Segments qu'on n'a pas su lire. Affiches a l'ingestion plutot que tus :
   *  un raccourci mal configure doit se voir des le premier matin. */
  ignores: string[]
}

const SEGMENT = /^([A-Z])(\d{12})$/

/**
 * Construit un `Instant` a partir d'un horodatage local `yyyyMMddHHmm`.
 * Rend `null` si la date n'existe pas (mois 13, 31 fevrier...).
 */
export function instantDepuisHorodatageLocal(brut: string): string | null {
  const annee = Number(brut.slice(0, 4))
  const mois = Number(brut.slice(4, 6))
  const jour = Number(brut.slice(6, 8))
  const heure = Number(brut.slice(8, 10))
  const minute = Number(brut.slice(10, 12))

  if (mois < 1 || mois > 12 || jour < 1 || jour > 31) return null
  if (heure > 23 || minute > 59) return null

  const d = new Date(annee, mois - 1, jour, heure, minute, 0, 0)
  // `new Date(2026, 1, 31)` ne leve pas : il glisse au 3 mars. On refuse ces
  // dates plutot que d'enregistrer un evenement au mauvais jour.
  if (d.getMonth() !== mois - 1 || d.getDate() !== jour) return null
  return d.toISOString()
}

/**
 * Decode une charge utile complete.
 *
 * Tolerante par construction : segments vides (le raccourci ajoute un tiret
 * final a chaque ecriture), espaces et retours a la ligne parasites, lettres
 * inconnues. Une charge partiellement illisible doit livrer ce qu'elle contient
 * de valide — perdre une nuit entiere parce qu'un segment est corrompu serait
 * disproportionne.
 */
export function decoderPayload(payload: string, maintenant = nowInstant()): ResultatDecodage {
  const evenements: Evenement[] = []
  const ignores: string[] = []
  const vus = new Set<string>()

  for (const brut of payload.split('-')) {
    const segment = brut.trim().toUpperCase()
    if (!segment) continue

    const m = SEGMENT.exec(segment)
    if (!m) {
      ignores.push(segment)
      continue
    }

    const type = TYPES_PAR_LETTRE[m[1] as string]
    const at = instantDepuisHorodatageLocal(m[2] as string)
    if (!type || !at) {
      ignores.push(segment)
      continue
    }

    // La cle est le couple type+instant : c'est elle qui rend une
    // resynchronisation idempotente, ici comme plus tard dans Dexie.
    const id = `${type}-${at}`
    if (vus.has(id)) continue
    vus.add(id)

    evenements.push({ id, type, at, creeLe: maintenant })
  }

  evenements.sort((a, b) => a.at.localeCompare(b.at))
  return { evenements, ignores }
}

/**
 * Extrait la charge utile d'une route `/sync/...`.
 *
 * Le segment est passe a `decodeURIComponent` car iOS peut echapper certains
 * caracteres, et react-router rend le parametre deja decode une fois.
 */
export function payloadDepuisRoute(param: string | undefined): string {
  if (!param) return ''
  try {
    return decodeURIComponent(param)
  } catch {
    return param
  }
}
